import React, { useState, useEffect } from 'react';
import { DataGrid, useGridApiRef } from '@mui/x-data-grid';
import { GridFooterContainer } from '@mui/x-data-grid';
import { Box, Button, DialogContent, Select, MenuItem, Popover, Divider, Typography, FormControl, Pagination, PaginationItem } from '@mui/material';
// import Papa from 'papaparse';
import { useFormik } from 'formik';
import '../components/DeviceTable.css';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/system';
import CustomTextField from '../components/CustomTextField';
import CustomSelect from '../components/CustomSelect';
import koKR from '../components/koKR.json'; // Import the translation file
import DiagResultsInfoDialog from './DiagResultsInfoDialog';
import CustomColumnSortedAscendingIcon from '../components/CustomColumnSortedAscendingIcon';
import CustomColumnSortedDescendingIcon from '../components/CustomColumnSortedDescendingIcon';
import apiClient from '../api/apiClient'; // API client import
import Config from '../Config'; // apiUrl 추가
import { getAccessToken } from '../utils/token';

const apiUrl = Config.apiUrl;

function CustomPagination(props) {
    const pageCount = Math.ceil(props.totalElements / props.size);
    const rowsPerPage = props.size;

    const handlePageChange = (event, value) => {
        props.onPageChange(value - 1); // DataGrid는 0-based page, Pagination은 1-based page
    };

    const handlePageSizeChange = (event) => {
        props.onPageSizeChange(parseInt(event.target.value, 10));
    };

    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 16px', width: '100%', marginBottom: '25px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body1">Showing</Typography>
                <FormControl variant="outlined" sx={{ mx: 1, minWidth: 78, width: 78, height: 38 }}>
                    <Select value={rowsPerPage} onChange={handlePageSizeChange}
                        disableUnderline
                        sx={{ fontSize: '0.875rem', height: '100%', paddingTop: '8px', paddingBottom: '8px' }}>
                        {[10, 20, 30, 50].map((size) => (
                            <MenuItem key={size} value={size}>
                                {size}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Typography variant="body1">of {props.totalElements}</Typography>
            </Box>
            {props.totalElements > 0 && (
                <Pagination
                    count={pageCount}
                    page={props.page + 1}
                    onChange={handlePageChange}
                    siblingCount={1}
                    boundaryCount={1}
                    shape="rounded"
                    color="primary"
                    variant='outlined'
                    renderItem={(item) => (
                        <PaginationItem
                            {...item}
                            sx={{
                                '&.Mui-selected': {
                                    backgroundColor: '#1976d2',
                                    color: '#fff',
                                },
                                backgroundColor: item.selected ? 'initial' : '#e0e0e0',
                                color: item.selected ? '#fff' : '#8b909a', // 선택되지 않았을 때 글자색 설정
                                border: 'none',
                                width: '28px',    // 가로 크기 설정
                                height: '28px',   // 세로 크기 설정
                                margin: '1px',    // 박스 간 간격 설정 (전체 간격이 2px이므로 양쪽에 1px씩)
                            }}
                        />
                    )}
                />
            )}
        </Box>
    );
}

const NoRowsOverlay = styled('div')({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    fontSize: '1.5rem',
    color: '#888',
    flexDirection: 'column',
});

function CustomFooter(props) {
    return (
        <GridFooterContainer sx={{ borderTop: 'none', color: '#8b909a' }}>
            <CustomPagination
                page={props.page}
                size={props.size}
                totalElements={props.totalElements}
                onPageChange={props.onPageChange}
                onPageSizeChange={props.onPageSizeChange}
            />
        </GridFooterContainer>
    );
}

const DiagResults = () => {
    const [rows, setRows] = useState([]);
    const [openFilterDialog, setOpenFilterDialog] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const apiRef = useGridApiRef();
    const { i18n } = useTranslation();
    const { t } = useTranslation('console');
    const getRowHeight = (params) => 58;
    const [selectionModel, setSelectionModel] = React.useState([]);
    const [selectedRowId, setSelectedRowId] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);

    // Pagination and sorting states
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0); // 0-based page index
    const [size, setSize] = useState(10); // Page size
    const [sort, setSort] = useState([
        "date,desc",
        "serial,asc",
        "plungerMotor,desc",
        "cameraBLU,asc",
        "tempIC,desc",
        "cartridgeSensor,asc",
        "eqc,desc"
    ]); // Default sort
    const [totalElements, setTotalElements] = useState(0); // Total number of elements for pagination

    // Filter states
    const [filterRequest, setFilterRequest] = useState({
        serial: '',
        plungerMotor: "",
        cameraBlu: "",
        tempIc: "",
        cartridgeSensor: "",
        eqc: ""
    });

    // API 데이터 호출
    const fetchDiagResults = async () => {
        setLoading(true);
        try {
            const token = getAccessToken();

            const requestParams = {};
            // filterRequest에서 값이 있는 필드만 추가
            if (filterRequest.serial) requestParams.serial = filterRequest.serial;
            if (filterRequest.plungerMotor !== "") requestParams.plungerMotor = filterRequest.plungerMotor;
            if (filterRequest.cameraBlu !== "") requestParams.cameraBlu = filterRequest.cameraBlu;
            if (filterRequest.tempIc !== "") requestParams.tempIc = filterRequest.tempIc;
            if (filterRequest.cartridgeSensor !== "") requestParams.cartridgeSensor = filterRequest.cartridgeSensor;
            if (filterRequest.eqc !== "") requestParams.eqc = filterRequest.eqc;

            const response = await apiClient.get(`${apiUrl}/console/self-tests`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                params: {
                    ...requestParams, // 필터링 파라미터
                    page: page,
                    size: size,
                    sort: sort, // 정렬 파라미터 (배열 그대로 전달)
                },
            });

            const data = response.data.content;

            // API 데이터 매핑
            const mappedRows = data.map((item) => ({
                id: item.id,
                diag_type: item.diagType, // 진단 타입 필드가 API에 있다고 가정
                date: item.date,
                serial: item.serial,
                plungerMotor: item.plungerMotor, // API 필드명과 일치
                cameraBlu: item.cameraBlu, // API 필드명과 일치
                tempIc: item.tempIc, // API 필드명과 일치
                cartridgeSensor: item.cartridgeSensor, // API 필드명과 일치
                eqc: item.eqc, // API 필드명과 일치
            }));

            setRows(mappedRows);
            setTotalElements(response.data.totalElements); // 총 데이터 수 설정
        } catch (error) {
            console.error('Error fetching Diag results:', error);
        } finally {
            setLoading(false);
        }
    };

    // API 데이터 호출 useEffect
    useEffect(() => {
        fetchDiagResults();
    }, [page, size, sort, filterRequest]);

    const handleRowClick = (params) => {
        setSelectedRowId(params.id);  // 선택된 행의 ID를 저장
        setDialogOpen(true);  // 다이얼로그 열기
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
    };

    function renderStatus(params) {
        console.log(params.value);
        return (
            <Box sx={{ textAlign: 'center', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {params.value ? <img src="/icon-pass.png" alt="pass" /> : <img src="/icon-fail.png" alt="fail" />}
            </Box>
        );
    }

    const columns = [
        { field: 'id', headerName: `${t('self_diag.column.id')}`, flex: 1, minWidth: 70, headerAlign: 'center', align: 'center' },
        { field: 'date', headerName: `${t('self_diag.column.diag_time')}`, flex: 1.5, minWidth: 100, headerAlign: 'center', align: 'center' },
        { field: 'serial', headerName: `${t('self_diag.column.serial')}`, flex: 1.5, minWidth: 100, headerAlign: 'center', align: 'center' },
        { field: 'plungerMotor', headerName: `${t('self_diag.column.plunger_motor')}`, flex: 1, minWidth: 100, headerAlign: 'center', align: 'center', renderCell: (params) => renderStatus(params) },
        { field: 'cameraBlu', headerName: `${t('self_diag.column.camera_blu')}`, flex: 1, minWidth: 100, headerAlign: 'center', align: 'center', renderCell: (params) => renderStatus(params) },
        { field: 'tempIc', headerName: `${t('self_diag.column.temp_ic')}`, flex: 1, minWidth: 100, headerAlign: 'center', align: 'center', renderCell: (params) => renderStatus(params) },
        { field: 'cartridgeSensor', headerName: `${t('self_diag.column.cat_sensor')}`, flex: 1, minWidth: 100, headerAlign: 'center', align: 'center', renderCell: (params) => renderStatus(params) },
        { field: 'eqc', headerName: `${t('self_diag.column.eqc')}`, flex: 1, minWidth: 100, headerAlign: 'center', align: 'center', renderCell: (params) => renderStatus(params) },
    ];

    const formik = useFormik({
        initialValues: {
            serial: '',
            plungerMotor: "",
            cameraBlu: "",
            tempIc: "",
            cartridgeSensor: "",
            eqc: "",
        },
        onSubmit: (values) => {
            setFilterRequest({
                serial: values.serial,
                plungerMotor: values.plungerMotor,
                cameraBlu: values.cameraBlu,
                tempIc: values.tempIc,
                cartridgeSensor: values.cartridgeSensor,
                eqc: values.eqc,
            });
            setPage(0); // 검색 시 첫 페이지로 이동
            handleCloseFilterDialog(); // 필터 다이얼로그 닫기
        },
    });

    const handleExport = () => {
        apiRef.current.exportDataAsCsv();
    };

    // 'View All' 버튼 클릭 시 전체 데이터 복원
    const handleViewAll = () => {
        setFilterRequest({
            serial: '',
            plungerMotor: "",
            cameraBlu: "",
            tempIc: "",
            cartridgeSensor: "",
            eqc: ""
        });
        setPage(0); // 첫 페이지로 이동
        formik.resetForm(); // 필터 검색 폼의 입력값과 선택값 초기화
    };

    // 'Filter Search' 버튼 클릭 시 필터 다이얼로그 토글
    const handleFilterSearch = (event) => {
        setAnchorEl(event.currentTarget);
        setOpenFilterDialog(!openFilterDialog);
    };

    const handleCloseFilterDialog = () => {
        setOpenFilterDialog(false);
        setAnchorEl(null);
    };

    //menuItems for plunger motor
    const plungerMenuItems = [
        { value: "", label: "전체" },
        { value: true, label: "통과" },
        { value: false, label: "실패" }
    ];

    //menuItems for camera blu
    const cameraMenuItems = [
        { value: "", label: "전체" },
        { value: true, label: "통과" },
        { value: false, label: "실패" }
    ];

    //menuItems for temp ic
    const tempMenuItems = [
        { value: "", label: "전체" },
        { value: true, label: "통과" },
        { value: false, label: "실패" }
    ];

    //menuItems for cat sensor
    const catMenuItems = [
        { value: "", label: "전체" },
        { value: true, label: "통과" },
        { value: false, label: "실패" }
    ];

    //menuItems for eqc
    const eqcMenuItems = [
        { value: "", label: "전체" },
        { value: true, label: "통과" },
        { value: false, label: "실패" }
    ];
    /* TODO: i18n 추후 적용
    //menuItems for plunger motor
    const plungerMenuItems = [
        { value: "", label: t('self_diag.filter_search.options.all') },
        { value: true, label: t('self_diag.filter_search.options.pass') },
        { value: false, label: t('self_diag.filter_search.options.fail') }
    ];

    //menuItems for camera blu
    const cameraMenuItems = [
        { value: "", label: t('self_diag.filter_search.options.all') },
        { value: true, label: t('self_diag.filter_search.options.pass') },
        { value: false, label: t('self_diag.filter_search.options.fail') }
    ];

    //menuItems for temp ic
    const tempMenuItems = [
        { value: "", label: t('self_diag.filter_search.options.all') },
        { value: true, label: t('self_diag.filter_search.options.pass') },
        { value: false, label: t('self_diag.filter_search.options.fail') }
    ];

    //menuItems for cat sensor
    const catMenuItems = [
        { value: "", label: t('self_diag.filter_search.options.all') },
        { value: true, label: t('self_diag.filter_search.options.pass') },
        { value: false, label: t('self_diag.filter_search.options.fail') }
    ];

    //menuItems for eqc
    const eqcMenuItems = [
        { value: "", label: t('self_diag.filter_search.options.all') },
        { value: true, label: t('self_diag.filter_search.options.pass') },
        { value: false, label: t('self_diag.filter_search.options.fail') }
    ];
    */

    const getLocaleText = () => {
        return i18n.language === 'ko' ? koKR : {};
    };

    return (
        <Box sx={{ width: '1592px', minWidth: 1024 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h2" sx={{ fontSize: '32px', color: '#002a70', fontWeight: 'bold', mt: '20px' }}>
                    {t('self_diag.header.title')}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', width: '1524px' }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', marginBottom: '-10px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Button
                            onClick={handleViewAll}
                            style={{
                                fontSize: '16px',
                                width: '178px',
                                backgroundColor: 'white',
                                border: '1px solid #ccc',
                                borderRadius: '12px 0 0 0',
                                height: '48px',
                                justifyContent: 'flex-start', // Align text to the left
                                paddingLeft: '16px', // Add some padding to the left
                                color: '#7d7d7d',
                                fontWeight: 'bold',
                            }}
                        >
                            {t('button.view_all')}
                        </Button>
                        <Button
                            onClick={handleFilterSearch}
                            style={{
                                fontSize: '16px',
                                width: '211px',
                                backgroundColor: 'white',
                                border: '1px solid #ccc',
                                borderRadius: '0 12px 0 0',
                                height: '48px',
                                justifyContent: 'flex-start', // Align text to the left
                                paddingLeft: '16px', // Add some padding to the left
                                paddingRight: '18px', // Add some padding to the right for endIcon
                                color: '#7d7d7d',
                                fontWeight: 'bold',
                            }}
                            startIcon={<img src={'/filter.png'} alt="Filter Icon" style={{ width: '30px', height: '30px' }} />}
                            endIcon={openFilterDialog ? <img src={'/up.png'} alt="Arrow Icon" style={{ width: '30px', height: '30px', marginLeft: '40px' }} /> :
                                <img src={'/down.png'} alt="Arrow Icon" style={{ width: '30px', height: '30px', marginLeft: '40px' }} />}
                        >
                            {t('button.filter_search')}
                        </Button>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '10px', width: 'calc(100% - 389px - 10px)' }}>
                    <Box className="button_container" sx={{ display: 'flex', gap: '10px' }}>
                        <Button
                            variant="contained"
                            onClick={handleExport}
                            className="button_dwload"
                            sx={{
                                fontSize: '14px',
                                borderRadius: '10px',
                                textTransform: 'none',
                                backgroundColor: '#80befc',
                                '&:hover': {
                                    backgroundColor: '#0b5cd5',
                                },
                            }}
                            endIcon={<img src={'/download.png'} alt="Download Icon" style={{ width: '30px', height: '30px' }} />}
                        >
                            {t('button.download')}
                        </Button>
                    </Box>
                </Box>
            </Box>
            <Box sx={{ width: '1524px', height: '756px', mt: 0 }}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    apiRef={apiRef}
                    getRowId={(row) => row.id}
                    getRowHeight={getRowHeight}
                    onRowClick={handleRowClick}  // 행 클릭 시 이벤트 핸들러 호출
                    headerHeight={48}
                    localeText={getLocaleText()} // Use the localeText based on the current locale
                    loading={loading}
                    rowCount={totalElements}
                    pageSizeOptions={[10, 20, 30, 50]}
                    paginationModel={{ page, pageSize: size }}
                    onPaginationModelChange={(newModel) => {
                        setPage(newModel.page);
                        setSize(newModel.pageSize);
                    }}
                    sortingMode="server"
                    sortModel={sort.map(s => ({ field: s.split(',')[0], sort: s.split(',')[1] }))}
                    onSortModelChange={(newSortModel) => {
                        setSort(newSortModel.map(s => `${s.field},${s.sort}`));
                    }}
                    paginationMode="server"
                    slots={{
                        footer: () => (
                            <CustomFooter
                                page={page}
                                size={size}
                                totalElements={totalElements}
                                onPageChange={setPage}
                                onPageSizeChange={setSize}
                            />
                        ),
                        noRowsOverlay: () => (loading ? null : (
                            <NoRowsOverlay>
                                <img src="nodata.png" alt="No data" />
                            </NoRowsOverlay>
                        )),
                        columnSortedAscendingIcon: CustomColumnSortedAscendingIcon,
                        columnSortedDescendingIcon: CustomColumnSortedDescendingIcon,
                    }}
                    initialState={{
                        pagination: { paginationModel: { pageSize: 10 } },
                    }}
                    disableSelectionOnClick
                    selectionModel={selectionModel}
                    onSelectionModelChange={(newSelection) => {
                        setSelectionModel(newSelection);
                    }}
                    sx={{
                        '& .MuiDataGrid-columnHeaders div[role="row"]': {
                            backgroundColor: '#F5F5F7',
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            textAlign: 'center',
                            display: 'flex',
                            justifyContent: 'center',
                            width: '100%',
                            fontSize: '14px',
                            fontWeight: '800',
                            color: '#7d7d7d',
                        },
                        '& .MuiDataGrid-cell': {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            fontSize: '16px',
                            color: '#494949',
                            height: '58px',
                            backgroundColor: '#ffffff',
                        },
                        '& .MuiDataGrid-cellContent': {
                            width: '100%',
                        },
                        '& .MuiDataGrid-footerContainer': {
                            display: 'flex',
                            justifyContent: 'center',
                            marginTop: '50px',
                            backgroundColor: '#ffffff',
                        },
                        borderBottomLeftRadius: '14px',
                        borderBottomRightRadius: '14px',
                        overflow: 'hidden',
                        backgroundColor: '#ffffff',
                    }}
                />
                <DiagResultsInfoDialog
                    open={dialogOpen}
                    onClose={handleCloseDialog}
                    rowId={selectedRowId}
                />
            </Box>
            <Popover
                open={openFilterDialog}
                anchorEl={anchorEl}
                onClose={handleCloseFilterDialog}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                PaperProps={{
                    style: {
                        width: 643,
                        height: 480, // Adjusted height for additional filter fields
                        borderRadius: '8px',
                        border: '1px solid #80befc',
                    },
                }}
            >
                <DialogContent>
                    <form onSubmit={formik.handleSubmit}>
                        <Box sx={{ mt: '10px', ml: '21px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{ display: 'flex', gap: '24px' }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                    <Typography sx={{ color: '#002a70' }}>{t('self_diag.filter_search.serial')}</Typography>
                                    <CustomTextField
                                        id="serial"
                                        name="serial"
                                        placeholder={t('self_diag.filter_search.serial_placeholder')}
                                        error={false}
                                        disabled={false}
                                        value={formik.values.serial}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        active={true}
                                        size="medium"
                                        width="171px"   // 가로 크기 지정
                                        height="48px"   // 세로 크기 지정
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                    <Typography sx={{ color: '#002a70' }}>{t('self_diag.filter_search.plunger_motor')}</Typography>
                                    <CustomSelect
                                        id="plungerMotor"
                                        name="plungerMotor"
                                        value={formik.values.plungerMotor}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        menuItems={plungerMenuItems}
                                        placeholder={t('self_diag.filter_search.select')}
                                        width="171px"
                                        height="48px"
                                        itemWidth="151px"
                                        itemHeight="42px"
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                    <Typography sx={{ color: '#002a70' }}>{t('self_diag.filter_search.camera_blu')}</Typography>
                                    <CustomSelect
                                        id="cameraBlu"
                                        name="cameraBlu"
                                        value={formik.values.cameraBlu}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        menuItems={cameraMenuItems}
                                        placeholder={t('self_diag.filter_search.select')}
                                        width="171px"
                                        height="48px"
                                        itemWidth="151px"
                                        itemHeight="42px"
                                    />
                                </Box>
                            </Box>
                            <Divider
                                sx={{
                                    width: '561px',
                                    margin: '10px 0 8px',
                                    backgroundColor: 'var(--gray-300)',
                                }}
                            />
                            <Box sx={{ display: 'flex', gap: '24px' }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                    <Typography sx={{ color: '#002a70' }}>{t('self_diag.filter_search.temp_ic')}</Typography>
                                    <CustomSelect
                                        id="tempIc"
                                        name="tempIc"
                                        value={formik.values.tempIc}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        menuItems={tempMenuItems}
                                        placeholder={t('self_diag.filter_search.select')}
                                        width="171px"
                                        height="48px"
                                        itemWidth="151px"
                                        itemHeight="42px"
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                    <Typography sx={{ color: '#002a70' }}>{t('self_diag.filter_search.cat_sensor')}</Typography>
                                    <CustomSelect
                                        id="cartridgeSensor"
                                        name="cartridgeSensor"
                                        value={formik.values.cartridgeSensor}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        menuItems={catMenuItems}
                                        placeholder={t('self_diag.filter_search.select')}
                                        width="171px"
                                        height="48px"
                                        itemWidth="151px"
                                        itemHeight="42px"
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                    <Typography sx={{ color: '#002a70' }}>{t('self_diag.filter_search.eqc')}</Typography>
                                    <CustomSelect
                                        id="eqc"
                                        name="eqc"
                                        value={formik.values.eqc}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        menuItems={eqcMenuItems}
                                        placeholder={t('self_diag.filter_search.select')}
                                        width="171px"
                                        height="48px"
                                        itemWidth="151px"
                                        itemHeight="42px"
                                    />
                                </Box>
                            </Box>
                            <Box sx={{ mt: '40px', display: 'flex', justifyContent: 'center', gap: '28px' }}>
                                <Button
                                    onClick={handleCloseFilterDialog}
                                    variant="outlined"
                                    sx={{ color: '#8b8fa8', border: 'solid 1px var(--gray-gray-200)', fontSize: '16px', width: '230px', height: '48px', borderRadius: '10px' }}
                                >
                                    {t('button.cancel')}
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    sx={{ backgroundColor: "#007dfa", fontSize: '16px', width: '230px', height: '48px', borderRadius: '10px' }}
                                >
                                    {t('button.search')}
                                </Button>
                            </Box>
                        </Box>
                    </form>
                </DialogContent>
            </Popover>
        </Box>
    );
};

export default DiagResults;